import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Copy, Check, Mail, PhoneMissed, Bot, Loader2,
  Sparkles, ShieldCheck, ShieldAlert, ShieldQuestion, KeyRound,
  RefreshCw, Eye, EyeOff, BarChart3, Clock, Globe2, X,
  PartyPopper, PenLine, Server,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const httpAuth = () => ({ Authorization: `Bearer ${localStorage.getItem("wt_token")}` });

const AI_MODES = [
  { id: "assist", label: "Assist", icon: ShieldQuestion,
    tone: "border-slate-300 bg-white text-slate-900",
    activeTone: "border-slate-900 bg-slate-900 text-white",
    line: "AI drafts only when you click the wand. Nothing sends without you.",
  },
  { id: "supervised", label: "Supervised", icon: ShieldAlert,
    tone: "border-amber-200 bg-white text-amber-900",
    activeTone: "border-amber-500 bg-amber-500 text-white",
    line: "AI auto-drafts a reply on every new message. You review and send.",
  },
  { id: "autonomous", label: "Autonomous", icon: ShieldCheck,
    tone: "border-emerald-200 bg-white text-emerald-900",
    activeTone: "border-emerald-600 bg-emerald-600 text-white",
    line: "AI auto-sends replies on web-form + chat after a 60s hold. Reply yourself to cancel.",
  },
];

export default function ConversationHubSettings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [snippet, setSnippet] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [copiedKey, setCopiedKey] = useState("");
  const [savingMode, setSavingMode] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [webhook, setWebhook] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [mx, setMx] = useState(null);
  const [checkingMx, setCheckingMx] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [digest, setDigest] = useState(null);   // { tone, note, allowed_tones, tone_examples }
  const [savingDigest, setSavingDigest] = useState(false);
  const [redisInfra, setRedisInfra] = useState(null);
  const isAdmin = user?.role === "admin" || /@websitetech\.pro$/i.test(user?.email || "");

  useEffect(() => { if (user === null) navigate("/login"); }, [user, navigate]);

  const loadAll = useCallback(async () => {
    try {
      const [a, b, c, d, e, f] = await Promise.all([
        axios.get(`${API}/hub/settings`, { headers: httpAuth() }),
        axios.get(`${API}/hub/widget/snippet`, { headers: httpAuth() }),
        axios.get(`${API}/hub/email/status`, { headers: httpAuth() }),
        axios.get(`${API}/hub/webhook-secret`, { headers: httpAuth() }),
        axios.get(`${API}/hub/analytics/autonomy?days=7`, { headers: httpAuth() }),
        axios.get(`${API}/hub/settings/digest`, { headers: httpAuth() }),
      ]);
      setSettings(a.data);
      setSnippet(b.data);
      setEmailStatus(c.data);
      setWebhook(d.data);
      setAnalytics(e.data);
      setDigest(f.data);
    } catch {
      toast.error("Could not load settings");
    }
  }, []);

  useEffect(() => { if (user?.email) loadAll(); }, [user, loadAll]);

  useEffect(() => {
    if (isAdmin) {
      axios.get(`${API}/admin/hub/infra/redis`, { headers: httpAuth() })
        .then((r) => setRedisInfra(r.data)).catch(() => {});
    }
  }, [isAdmin]);

  const setMode = async (mode) => {
    setSavingMode(true);
    try {
      await axios.post(`${API}/hub/settings/ai-mode`,
        { ai_mode: mode }, { headers: httpAuth() });
      setSettings((s) => ({ ...(s || {}), ai_mode: mode }));
      toast.success(`AI mode set to ${mode}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save AI mode");
    } finally { setSavingMode(false); }
  };

  const copy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(""), 1600);
      toast.success("Copied to clipboard");
    } catch { toast.error("Copy failed"); }
  };

  const connectEmail = async () => {
    setConnecting(true);
    try {
      const r = await axios.post(`${API}/hub/email/connect`,
        { business_email: emailInput.trim(), display_name: displayName.trim() },
        { headers: httpAuth() });
      toast.success("Business email connected");
      setEmailInput("");
      setEmailStatus((s) => ({ ...(s || {}), connected: true,
        business_email: emailInput.trim(), display_name: displayName.trim(),
        forwarding_alias: r.data.forwarding_alias }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not connect email");
    } finally { setConnecting(false); }
  };

  const disconnectEmail = async () => {
    if (!window.confirm("Disconnect this email?")) return;
    try {
      await axios.post(`${API}/hub/email/disconnect`, {}, { headers: httpAuth() });
      setEmailStatus((s) => ({ ...(s || {}), connected: false,
        business_email: "", display_name: "" }));
      toast.success("Email disconnected");
    } catch { toast.error("Disconnect failed"); }
  };

  const rotateSecret = async () => {
    if (!window.confirm("Rotate webhook secret? The old secret will stop working immediately.")) return;
    setRotating(true);
    try {
      const r = await axios.post(`${API}/hub/webhook-secret/rotate`, {},
        { headers: httpAuth() });
      setWebhook((w) => ({ ...(w || {}), secret: r.data.secret }));
      setShowSecret(true);
      toast.success("Webhook secret rotated");
    } catch { toast.error("Rotate failed"); }
    finally { setRotating(false); }
  };

  const runMxCheck = async () => {
    setCheckingMx(true);
    try {
      const r = await axios.get(`${API}/hub/email/mx-check`, { headers: httpAuth() });
      setMx(r.data);
      if (r.data.just_went_live) {
        toast.success("🎉 You're LIVE — inbound emails will now land in your Hub", {
          duration: 6000,
        });
      } else if (r.data.all_ok) {
        toast.success("DNS verified — inbox is live!");
      } else {
        toast.info("DNS check complete — see results below");
      }
      const s = await axios.get(`${API}/hub/email/status`, { headers: httpAuth() });
      setEmailStatus(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "DNS check failed");
    } finally { setCheckingMx(false); }
  };

  const saveDigest = async (patch) => {
    setSavingDigest(true);
    try {
      const r = await axios.post(`${API}/hub/settings/digest`, patch,
        { headers: httpAuth() });
      setDigest((d) => ({ ...(d || {}),
        tone: r.data.digest_tone ?? d?.tone,
        note: r.data.digest_note ?? d?.note,
        _dirty: false }));
      toast.success("Digest preferences saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingDigest(false); }
  };

  const pingRedis = async () => {
    try {
      const r = await axios.post(`${API}/admin/hub/infra/redis/ping`, {},
        { headers: httpAuth() });
      toast[r.data.ok ? "success" : "info"](
        r.data.ok ? "Redis ping OK" : `Redis unreachable: ${r.data.reason}`);
      // Refresh state
      const s = await axios.get(`${API}/admin/hub/infra/redis`, { headers: httpAuth() });
      setRedisInfra(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ping failed");
    }
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const r = await axios.get(`${API}/hub/analytics/autonomy/digest-preview?days=7`,
        { headers: httpAuth() });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setPreviewLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="hub-settings-page">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 py-6">
        <Link to="/hub/inbox"
              className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-3"
              data-testid="hub-settings-back">
          <ArrowLeft className="w-4 h-4" /> Back to inbox
        </Link>
        <div className="text-[11px] uppercase tracking-widest text-orange-600 font-bold">Conversation Hub</div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">Setup &amp; automation</h1>

        {/* ------------------------------------------------------------------ */}
        {/* 1. Widget Snippet                                                  */}
        {/* ------------------------------------------------------------------ */}
        <section className="bg-white ring-1 ring-slate-200 rounded-xl p-5 mb-5"
                 data-testid="hub-widget-section">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-orange-50 text-orange-600 grid place-items-center">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">One-line install snippet</h2>
              <p className="text-sm text-slate-600 mt-0.5">
                Paste this <code className="bg-slate-100 px-1 rounded">&lt;script&gt;</code> tag once on any WordPress, Wix, Squarespace or plain HTML site.
                Every form marked <code className="bg-slate-100 px-1 rounded">data-wt-hub</code> will feed the Hub instantly.
              </p>
            </div>
          </div>

          {!snippet ? (
            <div className="text-slate-400 py-6"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>
          ) : (
            <>
              <div className="rounded-lg bg-slate-950 text-emerald-200 text-xs font-mono px-3 py-3 overflow-x-auto flex items-center justify-between gap-3"
                   data-testid="hub-widget-snippet">
                <code>{snippet.snippet}</code>
                <button onClick={() => copy("snippet", snippet.snippet)}
                        className="shrink-0 inline-flex items-center gap-1 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200 px-2 py-1 rounded"
                        data-testid="hub-widget-copy-btn">
                  {copiedKey === "snippet"
                    ? <><Check className="w-3 h-3" /> Copied</>
                    : <><Copy className="w-3 h-3" /> Copy</>}
                </button>
              </div>

              <details className="mt-3 text-sm text-slate-600" data-testid="hub-widget-with-chat">
                <summary className="cursor-pointer text-slate-900 font-medium">
                  Also enable the floating chat bubble
                </summary>
                <div className="rounded-lg bg-slate-950 text-emerald-200 text-xs font-mono px-3 py-3 mt-2 overflow-x-auto">
                  <pre className="whitespace-pre-wrap m-0">{snippet.snippet_with_chat}</pre>
                </div>
                <button onClick={() => copy("chat", snippet.snippet_with_chat)}
                        className="mt-2 text-xs inline-flex items-center gap-1 text-indigo-700 hover:text-indigo-900">
                  {copiedKey === "chat" ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy with chat bubble</>}
                </button>
              </details>

              <ul className="mt-4 space-y-1 text-sm text-slate-600 list-disc pl-5">
                {snippet.install_hints.map((h, i) => <li key={i}>{h}</li>)}
              </ul>
            </>
          )}
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* 2. Email connection                                                */}
        {/* ------------------------------------------------------------------ */}
        <section className="bg-white ring-1 ring-slate-200 rounded-xl p-5 mb-5"
                 data-testid="hub-email-section">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-sky-50 text-sky-600 grid place-items-center">
              <Mail className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Connect a business email</h2>
              <p className="text-sm text-slate-600 mt-0.5">
                Route your real inbox into the Hub. Replies land in the same conversation thread — no more toggling tabs.
              </p>
            </div>
          </div>

          {!emailStatus ? (
            <div className="text-slate-400 py-6"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>
          ) : emailStatus.connected ? (
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="rounded-lg ring-1 ring-emerald-200 bg-emerald-50 p-3">
                <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-bold">Connected email</div>
                <div className="font-bold text-slate-900 mt-1" data-testid="hub-email-connected">
                  {emailStatus.business_email}
                </div>
                {emailStatus.display_name && (
                  <div className="text-xs text-slate-500">as “{emailStatus.display_name}”</div>
                )}
              </div>
              <div className="rounded-lg ring-1 ring-slate-200 bg-white p-3">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">
                  Forward your inbox to
                </div>
                <div className="font-mono text-sm text-slate-900 mt-1 flex items-center gap-2">
                  <span data-testid="hub-email-alias">{emailStatus.forwarding_alias}</span>
                  <button onClick={() => copy("alias", emailStatus.forwarding_alias)}
                          className="text-[11px] inline-flex items-center gap-1 text-indigo-700 hover:text-indigo-900">
                    {copiedKey === "alias" ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} copy
                  </button>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {emailStatus.ingest_status === "live"
                    ? "Live — inbound emails now route into your Hub."
                    : (<><b className="text-amber-700">Setup pending</b> — inbound email requires Resend inbound MX on <code>inbox.websitetech.pro</code> (see webhook + DNS instructions below). Reply threading via <code>In-Reply-To</code> is already wired.</>)}
                </div>
              </div>
              <button onClick={disconnectEmail}
                      className="text-xs text-rose-700 hover:text-rose-900 justify-self-start"
                      data-testid="hub-email-disconnect">
                Disconnect
              </button>

              {/* DNS wizard */}
              <div className="sm:col-span-2 rounded-lg ring-1 ring-slate-200 bg-slate-50 p-3"
                   data-testid="hub-mx-wizard">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 grid place-items-center shrink-0">
                    <Globe2 className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900">DNS setup wizard</div>
                    <div className="text-xs text-slate-600">
                      Add the two records below at your DNS host, then click <b>Check DNS</b>. Once both pass, your inbox flips to <b>Live</b> automatically.
                    </div>
                    <button onClick={runMxCheck}
                            disabled={checkingMx}
                            className="mt-2 text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-40"
                            data-testid="hub-mx-check-btn">
                      {checkingMx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                      Check DNS
                    </button>
                  </div>
                </div>

                <div className="mt-3 grid gap-2">
                  {[
                    { key: "mx", label: "MX record",
                      expected: mx?.expected ? `${mx.expected.mx_host} · pref ${mx.expected.mx_preference}` : "inbound-smtp.resend.com · pref 10",
                      ok: mx?.mx_ok ?? emailStatus?.mx_ok,
                      seen: mx?.mx_records_seen || [] },
                    { key: "txt", label: "TXT record",
                      expected: mx?.expected ? `starts with '${mx.expected.txt_prefix}'` : "starts with 'resend-verify='",
                      ok: mx?.txt_ok ?? emailStatus?.txt_ok,
                      seen: mx?.txt_records_seen || [] },
                  ].map((row) => (
                    <div key={row.key}
                         className={`rounded-lg ring-1 px-3 py-2 text-xs flex items-start gap-2 ${
                           row.ok ? "ring-emerald-200 bg-emerald-50 text-emerald-900"
                                  : "ring-slate-200 bg-white text-slate-700"}`}
                         data-testid={`hub-mx-row-${row.key}`}>
                      <div className={`w-4 h-4 rounded-full grid place-items-center shrink-0 ${
                        row.ok ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-400"}`}>
                        {row.ok ? <Check className="w-3 h-3" /> : <X className="w-2.5 h-2.5" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div><b>{row.label}</b> — expected: <code className="bg-slate-100 px-1 rounded">{row.expected}</code></div>
                        {mx && (
                          <div className="mt-1 text-slate-500">
                            Seen: {row.seen.length
                              ? <span className="font-mono break-all">{row.seen.slice(0, 2).join(" · ")}</span>
                              : <i>no records</i>}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {mx?.expected?.notes && (
                  <div className="mt-2 text-[11px] text-slate-500">{mx.expected.notes}</div>
                )}
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              <input value={emailInput}
                     onChange={(e) => setEmailInput(e.target.value)}
                     placeholder="owner@yourcompany.com"
                     className="text-sm rounded-lg ring-1 ring-slate-200 px-3 py-2 focus:ring-slate-400 outline-none"
                     data-testid="hub-email-input" />
              <input value={displayName}
                     onChange={(e) => setDisplayName(e.target.value)}
                     placeholder="Display name (optional)"
                     className="text-sm rounded-lg ring-1 ring-slate-200 px-3 py-2 focus:ring-slate-400 outline-none" />
              <button onClick={connectEmail}
                      disabled={connecting || !/@/.test(emailInput)}
                      className="sm:col-span-2 justify-self-start inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm disabled:opacity-40"
                      data-testid="hub-email-connect-btn">
                {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Connect email
              </button>
            </div>
          )}
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* 3. Missed-call recovery                                            */}
        {/* ------------------------------------------------------------------ */}
        <section className="bg-white ring-1 ring-slate-200 rounded-xl p-5 mb-5"
                 data-testid="hub-missedcall-section">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-rose-50 text-rose-600 grid place-items-center">
              <PhoneMissed className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Missed-call recovery</h2>
              <p className="text-sm text-slate-600 mt-0.5">
                Every missed call becomes a conversation with a friendly AI text-back drafted for you. No ring goes to voicemail.
              </p>
            </div>
          </div>
          <div className="rounded-lg bg-slate-50 ring-1 ring-slate-200 p-3 text-sm text-slate-700">
            <div className="font-semibold text-slate-900 mb-1">Point your carrier's missed-call webhook to:</div>
            <div className="font-mono text-xs bg-white ring-1 ring-slate-200 rounded px-2 py-1 inline-flex items-center gap-2"
                 data-testid="hub-missedcall-webhook">
              POST {process.env.REACT_APP_BACKEND_URL}/api/hub/calls/missed
              <button onClick={() => copy("mc", `${process.env.REACT_APP_BACKEND_URL}/api/hub/calls/missed`)}
                      className="text-indigo-700 hover:text-indigo-900">
                {copiedKey === "mc" ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              </button>
            </div>
            <div className="text-xs text-slate-500 mt-2">
              Payload: <code>{'{ caller_name, caller_phone, called_at?, duration_seconds? }'}</code>.
              Missed calls appear in your inbox with a pre-drafted text-back you can send in one click.
            </div>
            <div className="text-xs text-amber-700 mt-2">
              <b>Note:</b> SMS delivery itself is <b>MOCKED</b> — messages log in the Hub timeline, but outbound SMS provider isn't connected yet.
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* 4. Webhook signing secret                                           */}
        {/* ------------------------------------------------------------------ */}
        <section className="bg-white ring-1 ring-slate-200 rounded-xl p-5 mb-5"
                 data-testid="hub-webhook-section">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-violet-50 text-violet-600 grid place-items-center">
              <KeyRound className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-slate-900">Webhook signing secret</h2>
              <p className="text-sm text-slate-600 mt-0.5">
                Sign your missed-call and email inbound webhooks with HMAC-SHA256 so nobody else can spam your inbox. Widget submissions (browser) use rate limits instead.
              </p>
            </div>
          </div>
          {!webhook ? (
            <div className="text-slate-400 py-4"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>
          ) : (
            <>
              <div className="rounded-lg bg-slate-950 text-emerald-200 text-xs font-mono px-3 py-3 flex items-center justify-between gap-3"
                   data-testid="hub-webhook-secret">
                <code className="truncate">{showSecret ? webhook.secret : "•".repeat(Math.max(8, webhook.secret.length))}</code>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => setShowSecret((v) => !v)}
                          className="inline-flex items-center gap-1 bg-slate-800 hover:bg-slate-700 text-slate-100 px-2 py-1 rounded"
                          data-testid="hub-webhook-toggle-visibility">
                    {showSecret ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    {showSecret ? "Hide" : "Reveal"}
                  </button>
                  <button onClick={() => copy("secret", webhook.secret)}
                          className="inline-flex items-center gap-1 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200 px-2 py-1 rounded"
                          data-testid="hub-webhook-copy-btn">
                    {copiedKey === "secret" ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                  </button>
                  <button onClick={rotateSecret}
                          disabled={rotating}
                          className="inline-flex items-center gap-1 bg-rose-500/10 hover:bg-rose-500/20 text-rose-200 px-2 py-1 rounded disabled:opacity-40"
                          data-testid="hub-webhook-rotate-btn">
                    {rotating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    Rotate
                  </button>
                </div>
              </div>
              <div className="mt-3 text-xs text-slate-600 space-y-1">
                <div><b>Header:</b> <code className="bg-slate-100 px-1 rounded">{webhook.signature_header}: sha256=&lt;hex&gt;</code></div>
                <div><b>Algorithm:</b> {webhook.algorithm}</div>
                <div><b>Rate limits:</b> missed-call 30/min · email inbound 60/min · widget 60/min per (tenant, IP)
                  {webhook.rate_limit_backend && (
                    <span className={`ml-2 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${
                      webhook.rate_limit_backend === "redis"
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-amber-100 text-amber-800"}`}
                          data-testid="hub-ratelimit-backend">
                      backend: {webhook.rate_limit_backend}
                    </span>
                  )}
                </div>
                <div className="text-slate-500">{webhook.widget_endpoint_note}</div>
              </div>
              <details className="mt-3 text-xs text-slate-600" data-testid="hub-webhook-example">
                <summary className="cursor-pointer text-slate-900 font-medium">Signing example (bash)</summary>
                <pre className="mt-2 rounded-lg bg-slate-950 text-emerald-200 px-3 py-2 overflow-x-auto whitespace-pre-wrap">
{`# Compute signature over the exact JSON body
SIG=$(printf '%s' "$BODY" | openssl dgst -sha256 -hmac "$SECRET" -hex | awk '{print $2}')
curl -X POST "${webhook.protected_endpoints[0]}" \\
  -H "Content-Type: application/json" \\
  -H "X-Hub-Signature: sha256=$SIG" \\
  -d "$BODY"`}
                </pre>
              </details>
            </>
          )}
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* 5. Autonomy analytics (weekly digest)                               */}
        {/* ------------------------------------------------------------------ */}
        <section className="bg-white ring-1 ring-slate-200 rounded-xl p-5 mb-5"
                 data-testid="hub-analytics-section">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 grid place-items-center">
              <BarChart3 className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">AI autonomy · last 7 days</h2>
              <p className="text-sm text-slate-600 mt-0.5">
                A weekly email digest lands in your inbox every Monday when AI mode is Supervised or Autonomous.
              </p>
            </div>
          </div>
          {!analytics ? (
            <div className="text-slate-400 py-4"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { k: "ai_sent", label: "AI sent", tone: "text-slate-900" },
                  { k: "ai_drafted", label: "AI drafted", tone: "text-orange-600" },
                  { k: "owner_cancelled", label: "You cancelled", tone: "text-slate-500" },
                  { k: "minutes_saved_estimate", label: "Mins saved", tone: "text-emerald-700" },
                ].map(({ k, label, tone }) => (
                  <div key={k}
                       className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3 text-center"
                       data-testid={`hub-analytics-${k}`}>
                    <div className={`text-2xl font-extrabold ${tone}`}>{analytics[k] ?? 0}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-1">{label}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                <Clock className="w-3 h-3" />
                Avg first reply this week: <b className="text-slate-900">
                  {analytics.avg_first_reply_seconds != null
                    ? `${Math.floor(analytics.avg_first_reply_seconds / 60)}m ${analytics.avg_first_reply_seconds % 60}s`
                    : "—"}
                </b>
                · Owner replies: <b className="text-slate-900">{analytics.owner_replies_total}</b>
              </div>
              {settings?.ai_mode === "assist" && (
                <div className="mt-3 text-xs text-amber-800 bg-amber-50 ring-1 ring-amber-200 rounded-lg px-3 py-2">
                  Weekly digests only send while your AI mode is Supervised or Autonomous. Change it below to opt in.
                </div>
              )}

              {/* Digest editor — tone + personal note stitched by AI */}
              {digest && (
                <div className="mt-4 rounded-lg ring-1 ring-slate-200 bg-slate-50 p-3"
                     data-testid="hub-digest-editor">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-orange-50 text-orange-600 grid place-items-center shrink-0">
                      <PenLine className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900">Digest tone &amp; personal note</div>
                      <div className="text-xs text-slate-500">
                        Pick how the digest reads. Add a note and AI stitches it in as a one-line intro.
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="hub-digest-tone-row">
                    {(digest.allowed_tones || ["warm", "professional", "brief"]).map((t) => (
                      <button key={t}
                              onClick={() => saveDigest({ tone: t })}
                              disabled={savingDigest || digest.tone === t}
                              className={`text-xs px-3 py-1.5 rounded-full ring-1 capitalize ${
                                digest.tone === t
                                  ? "bg-slate-900 text-white ring-slate-900"
                                  : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-100"} disabled:opacity-70`}
                              data-testid={`hub-digest-tone-${t}`}>
                        {t}
                      </button>
                    ))}
                    <span className="text-[11px] text-slate-500 ml-1 self-center italic">
                      Example: {digest.tone_examples?.[digest.tone]}
                    </span>
                  </div>
                  <textarea value={digest.note || ""}
                            onChange={(e) => setDigest((d) => ({ ...d, note: e.target.value, _dirty: true }))}
                            onBlur={(e) => {
                              if (digest._dirty) saveDigest({ note: e.target.value });
                            }}
                            placeholder="Add a personal note (max ~600 chars). AI will weave this into the top of the email."
                            className="mt-3 w-full text-sm rounded-lg ring-1 ring-slate-200 px-3 py-2 focus:ring-slate-400 outline-none bg-white"
                            rows={3}
                            maxLength={600}
                            data-testid="hub-digest-note" />
                  <div className="text-[11px] text-slate-400 mt-1 text-right">
                    {(digest.note || "").length}/600
                  </div>
                </div>
              )}

              <div className="mt-3">
                <button onClick={loadPreview}
                        disabled={previewLoading}
                        className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-lg ring-1 ring-slate-200 bg-white text-slate-800 hover:bg-slate-50 disabled:opacity-40"
                        data-testid="hub-digest-preview-btn">
                  {previewLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                  Preview weekly digest
                </button>
              </div>
            </>
          )}
        </section>

        {/* Digest preview modal */}
        {preview && (
          <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4"
               onClick={() => setPreview(null)}
               data-testid="hub-digest-preview-modal">
            <div className="bg-white rounded-xl max-w-xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
                 onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">Preview · Monday 8:00 AM</div>
                  <div className="font-bold text-slate-900 truncate" data-testid="hub-digest-subject">{preview.subject}</div>
                </div>
                <button onClick={() => setPreview(null)}
                        className="p-1 text-slate-400 hover:text-slate-900"
                        data-testid="hub-digest-close">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto bg-slate-100 p-4">
                <div className="bg-white rounded-lg shadow-sm"
                     data-testid="hub-digest-html"
                     dangerouslySetInnerHTML={{ __html: preview.html }} />
              </div>
              <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between text-xs">
                <div className={preview.would_send_now ? "text-emerald-700" : "text-slate-500"}>
                  {preview.would_send_now
                    ? <><Check className="w-3.5 h-3.5 inline mr-1" /> This digest will send Monday morning.</>
                    : <span data-testid="hub-digest-skip-reason">Not sending: {preview.reason_if_skipped}</span>}
                </div>
                <div className="text-slate-400">
                  {preview.last_digest_at
                    ? <>Last sent: {new Date(preview.last_digest_at).toLocaleDateString()}</>
                    : "Never sent yet"}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ------------------------------------------------------------------ */}
        {/* 6. AI Autonomy modes                                                */}
        {/* ------------------------------------------------------------------ */}
        <section className="bg-white ring-1 ring-slate-200 rounded-xl p-5 mb-10"
                 data-testid="hub-ai-modes-section">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 grid place-items-center">
              <Bot className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">AI autonomy</h2>
              <p className="text-sm text-slate-600 mt-0.5">
                Pick exactly how much the AI answers without your approval.
              </p>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {AI_MODES.map(({ id, label, icon: Icon, tone, activeTone, line }) => {
              const active = settings?.ai_mode === id;
              return (
                <button key={id}
                        onClick={() => setMode(id)}
                        disabled={savingMode || active}
                        className={`text-left rounded-xl border-2 p-4 transition ${active ? activeTone : tone} ${savingMode ? "opacity-70" : "hover:shadow-md"}`}
                        data-testid={`hub-ai-mode-${id}`}>
                  <div className="flex items-center gap-2 font-bold text-base">
                    <Icon className="w-4 h-4" /> {label}
                    {active && <span className="ml-auto text-[10px] uppercase tracking-widest">Active</span>}
                  </div>
                  <div className={`text-xs mt-1 ${active ? "opacity-90" : "opacity-75"}`}>{line}</div>
                </button>
              );
            })}
          </div>
          {settings?.ai_mode === "autonomous" && (
            <div className="mt-3 text-xs text-emerald-800 bg-emerald-50 ring-1 ring-emerald-200 rounded-lg px-3 py-2">
              Autonomous mode is live for <b>web form</b> and <b>chat widget</b> replies. Email &amp; SMS still require your review.
            </div>
          )}
        </section>

        {/* ------------------------------------------------------------------ */}
        {/* Admin-only: Redis rate-limiter ops                                 */}
        {/* ------------------------------------------------------------------ */}
        {isAdmin && redisInfra && (
          <section className="bg-slate-950 text-slate-100 rounded-xl p-5 mb-10"
                   data-testid="hub-redis-ops-section">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-slate-800 text-emerald-300 grid place-items-center">
                <Server className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">Deploy · Admin</div>
                <h2 className="text-lg font-bold">Rate-limiter infrastructure</h2>
                <p className="text-sm text-slate-400 mt-0.5">
                  One-click Redis provisioning steps. The limiter degrades to per-process in-memory when no Redis is reachable — so misconfiguration never takes the API offline.
                </p>
              </div>
              <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full self-start ${
                redisInfra.connected
                  ? "bg-emerald-500/20 text-emerald-300"
                  : "bg-amber-500/20 text-amber-300"}`}
                    data-testid="hub-redis-status-badge">
                {redisInfra.connected ? `LIVE · ${redisInfra.latency_ms}ms` : `IN-MEMORY${redisInfra.redis_url_set ? " (unreachable)" : ""}`}
              </span>
            </div>
            <ol className="space-y-2 text-sm">
              {redisInfra.one_click_deploy_steps.map((s) => (
                <li key={s.step} className="rounded-lg bg-slate-900/70 ring-1 ring-slate-800 p-3"
                     data-testid={`hub-redis-step-${s.step}`}>
                  <div className="font-semibold text-slate-100">
                    Step {s.step}. {s.title}
                  </div>
                  {s.options && (
                    <ul className="mt-1 list-disc pl-5 text-xs text-slate-400 space-y-0.5">
                      {s.options.map((o, i) => <li key={i}>{o}</li>)}
                    </ul>
                  )}
                  {s.example && (
                    <div className="mt-1 text-xs font-mono bg-black/40 text-emerald-200 rounded px-2 py-1 overflow-x-auto">
                      {s.example}
                    </div>
                  )}
                  {s.verify && (
                    <div className="mt-1 text-[11px] text-slate-500 italic">Verify: {s.verify}</div>
                  )}
                </li>
              ))}
            </ol>
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={pingRedis}
                      className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30"
                      data-testid="hub-redis-ping-btn">
                <RefreshCw className="w-3.5 h-3.5" /> Test Redis connection
              </button>
              {redisInfra.error && (
                <span className="text-xs text-rose-300 self-center">{redisInfra.error}</span>
              )}
            </div>
            <div className="mt-3 text-[11px] text-slate-500">{redisInfra.notes}</div>
          </section>
        )}
      </main>
    </div>
  );
}
